import { useImperativeAlertDialog } from '@astryxdesign/core/AlertDialog';
import { router } from '@inertiajs/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * Guard global de navegación: cuando una página registra cambios sin guardar,
 * intercepta la navegación (cambio de página vía GET) y abre un `AlertDialog`
 * para confirmar el descarte. También cubre reload/cierre de pestaña con el
 * prompt nativo del navegador (`beforeunload`).
 *
 * Solo bloquea navegación GET. Las mutaciones (POST/PATCH/DELETE) nunca se
 * interceptan: son justamente la acción que el usuario quiere realizar.
 */

interface PendingVisitInfo {
  url: URL;
  method: 'get' | 'post' | 'put' | 'patch' | 'delete';
}

type Registry = Map<string, () => boolean>;

interface DirtyGuardContextValue {
  /** Registra un predicado de "sucio". Devuelve un cleanup que lo desregistra. */
  register: (id: string, isDirty: () => boolean) => () => void;
}

const DirtyGuardContext = createContext<DirtyGuardContextValue | null>(null);

let sequence = 0;

function nextId(): string {
  sequence += 1;
  return `dirty-guard-${sequence}`;
}

export function DirtyGuardProvider({ children }: { children: ReactNode }) {
  // El registro vive en una ref para evitar re-registrar los listeners de
  // Inertia/window cada vez que una página cambia su estado de dirty.
  const registry = useRef<Registry>(new Map());
  const pendingVisit = useRef<PendingVisitInfo | null>(null);
  // Flag para re-lanzar la visita tras confirmar sin entrar en loop.
  const isBypassing = useRef(false);
  const alert = useImperativeAlertDialog();
  // Fuerza el re-render cuando cambia el bypass, por si hace falta en el futuro.
  const [, forceRender] = useState(0);

  const hasDirty = useCallback(() => {
    for (const predicate of registry.current.values()) {
      if (predicate()) return true;
    }
    return false;
  }, []);

  const register = useCallback((id: string, isDirty: () => boolean) => {
    registry.current.set(id, isDirty);
    return () => {
      registry.current.delete(id);
    };
  }, []);

  const confirmDiscard = useCallback(() => {
    const visit = pendingVisit.current;
    if (!visit) {
      alert.hide();
      return;
    }

    isBypassing.current = true;
    forceRender((n) => n + 1);
    pendingVisit.current = null;
    alert.hide();
    // Re-lanzamos la visita original; `isBypassing` evita que el handler
    // `before` la vuelva a interceptar.
    router.visit(visit.url, { method: visit.method });
  }, [alert]);

  const cancelDiscard = useCallback(() => {
    pendingVisit.current = null;
    alert.hide();
  }, [alert]);

  // Intercepta la navegación Inertia (sidebar, <Link>, router.get, back/forward).
  useEffect(() => {
    const unsubscribe = router.on('before', (event) => {
      const visit = event.detail.visit;

      // Solo nos ocupamos de la navegación GET entre páginas.
      if (visit.method !== 'get') return;

      // Reset del bypass cuando arranca una nueva visita tras una confirmada.
      if (isBypassing.current) {
        isBypassing.current = false;
        forceRender((n) => n + 1);
        return;
      }

      if (!hasDirty()) return;

      pendingVisit.current = { url: visit.url, method: visit.method };
      alert.show({
        title: 'Tenés cambios sin guardar',
        description: 'Si salís ahora vas a perder los cambios que no guardaste.',
        cancelLabel: 'Quedarme acá',
        actionLabel: 'Descartar y salir',
        actionVariant: 'destructive',
        onAction: confirmDiscard,
      });
      return false; // cancela esta visita; se re-lanza al confirmar
    });

    return unsubscribe;
  }, [alert, hasDirty, confirmDiscard, cancelDiscard]);

  // Cierra el diálogo si el usuario cancela con Escape (AlertDialog llama onOpenChange(false)).
  // Manejamos el cierre vía el propio callback onAction/Escape del diálogo.
  useEffect(() => {
    // Cuando el diálogo se cierra sin confirmar (Escape / click fuera cancelable),
    // aseguramos que no quede una pendingVisit colgando.
    if (!alert.isOpen && pendingVisit.current && !isBypassing.current) {
      pendingVisit.current = null;
    }
  }, [alert.isOpen]);

  // Prompt nativo para reload (F5) y cierre de pestaña/ventana.
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasDirty()) {
        event.preventDefault();
        // Necesario en algunos navegadores para mostrar el prompt.
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasDirty]);

  return (
    <DirtyGuardContext.Provider value={{ register }}>
      {children}
      {alert.element}
    </DirtyGuardContext.Provider>
  );
}

/**
 * Registra el estado "sucio" de la página actual en el guard global.
 * Pasa un booleano (no una función) para evitar problemas de closures staleness.
 *
 * @example
 * const hasChanges = rowsHaveChanges(draft, serverRows);
 * useDirtyGuard(hasChanges);
 */
export function useDirtyGuard(isDirty: boolean) {
  const context = useContext(DirtyGuardContext);
  // ID estable por instancia; la ref mantiene el valor de dirty siempre fresco.
  const idRef = useRef<string>(nextId());
  const dirtyRef = useRef(isDirty);
  dirtyRef.current = isDirty;

  if (!context) {
    throw new Error('useDirtyGuard debe usarse dentro de <DirtyGuardProvider>');
  }

  // Registro una sola vez (al montar); el predicado lee de la ref actualizada.
  useEffect(() => {
    return context.register(idRef.current, () => dirtyRef.current);
  }, [context]);
}
